import { SKILL_MAP, MODE_SKILLS, BKT_PARAMS, bktPG, bktUpdate, bktLearn, initBkt, getBktLevel } from './bkt.js';
import { isGuess, isPatternTapping, calculateSQS, shouldEnterChillMode } from './guardrails.js';
import { wordMasteryUpdate } from './leitner.js';

export function createProfile(name, age, interests, conf, focus, goal) {
  return {
    id: Date.now(),
    name,
    age,
    interests: interests || [],
    conf: conf || "getting_there",
    focus: focus || "all",
    goal: goal || "independent",
    heroName: 'Explorer',
    ageBracket: age <= 6 ? 'early' : age <= 9 ? 'rising' : 'champion',
    tracker: createTracker(3),
    totalStars: 0
  };
}

export function createTracker(sl) {
  return {
    currentLevel: sl || 3,
    streak: 0,
    ms: { phonics: {c:0,t:0}, sight: {c:0,t:0}, stories: {c:0,t:0}, bubbles: {c:0,t:0} },
    weakWords: [],
    wordMastery: {},
    rounds: 0,
    roundsAtLevel: 0,
    bubbleUnlocked: false,
    log: [],
    aiProfile: null,
    bkt: null,
    chillMode: false,
    sessionQuality: [],
    levelHistory: []
  };
}

export function getAgeBracketDesc(profile) {
  const bracket = profile.ageBracket || (profile.age <= 6 ? 'early' : profile.age <= 9 ? 'rising' : 'champion');
  return bracket === 'early' ? 'young reader ages 4-6' : bracket === 'rising' ? 'developing reader ages 7-9' : 'advanced reader ages 10-12';
}

export function getHeroName(profile) { return profile.heroName || 'Explorer'; }

export function record(t, mode, word, correct, ms, age, childName) {
  const n = {...t, ms: {...t.ms}, log: [...t.log || []]};
  n.ms[mode] = {c: n.ms[mode].c + (correct ? 1 : 0), t: n.ms[mode].t + 1};
  if (!correct && word) n.weakWords = [...n.weakWords.filter(w => w !== word), word].slice(-20);
  else if (correct && word) n.weakWords = n.weakWords.filter(w => w !== word);
  n.log.push({m: mode, w: word || null, c: correct, ms: ms || 0, lv: n.currentLevel, t: Date.now(), chill: !!n.chillMode});
  if (n.log.length > 50) n.log = n.log.slice(-50);
  const guessed = isGuess(ms, mode);
  const patternTap = isPatternTapping(n.log);
  if (guessed || patternTap) {
    n.log[n.log.length - 1].guessed = true;
  }
  if (word) {
    if (!n.wordMastery) n.wordMastery = {};
    if (!guessed && !patternTap) {
      n.wordMastery = wordMasteryUpdate(n.wordMastery, word, correct, age || 7, { childName });
    }
  }
  if (!guessed && !patternTap && !n.chillMode) {
    if (!n.bkt) n.bkt = initBkt(n.currentLevel);
    const skill = (MODE_SKILLS[mode] || {})[n.currentLevel] || SKILL_MAP[n.currentLevel]?.[0] || 'print_concepts';
    const pG = bktPG(mode);
    const oldPL = n.bkt[skill] || 0.1;
    let newPL = bktUpdate(oldPL, correct, pG, BKT_PARAMS.pS);
    newPL = bktLearn(newPL, BKT_PARAMS.pT);
    n.bkt = {...n.bkt, [skill]: newPL};
  }
  if (correct) n.streak = Math.max(0, n.streak + 1);
  else n.streak = Math.min(0, n.streak - 1);
  return n;
}

export function doneRound(t) {
  const result = {...t, rounds: t.rounds + 1, roundsAtLevel: (t.roundsAtLevel || 0) + 1, bubbleUnlocked: true, _demotedThisRound: false};
  if (result.bkt && (result.roundsAtLevel || 0) >= 2) {
    const bktLevel = getBktLevel(result.bkt);
    if (bktLevel !== result.currentLevel) {
      result.levelHistory = [...(result.levelHistory || []), {level: bktLevel, from: result.currentLevel, date: new Date().toISOString()}];
      result.currentLevel = bktLevel;
      result.roundsAtLevel = 0;
    }
  }
  const sqs = calculateSQS(result.log);
  result.sessionQuality = [...(result.sessionQuality || []).slice(-10), sqs];
  if (sqs.isAnomalous || shouldEnterChillMode(result.log)) {
    result.chillMode = true;
    result.chillRoundsRemaining = 3;
  } else if (result.chillMode) {
    result.chillRoundsRemaining = Math.max(0, (result.chillRoundsRemaining || 1) - 1);
    if (result.chillRoundsRemaining <= 0) {
      result.chillMode = false;
    }
  }
  return result;
}
